import { AdvancedFilter, RelativeDateFilter, RelativeDateFilterTimeUnit, RelativeDateOperators } from "powerbi-models";
import { DateTime } from "luxon";
import { IntervalType } from "../hooks";
import { DateView } from "@mui/x-date-pickers-pro";

export enum ReportIntervals {
  lastMonth = "LastMonth",
  lastThreeMonths = "LastThreeMonths",
  lastQuarter = "LastQuarter",
  lastSixMonths = "LastSixMonths",
  yearToDate = "YearToDate",
  yearOverYear = "YearOverYear",
  customRange = "CustomRange",
  customRangeWithDays = "CustomRangeWithDays",
}

export const defaultAvailableIntervals = [
  ReportIntervals.lastThreeMonths,
  ReportIntervals.lastSixMonths,
  ReportIntervals.yearToDate,
  ReportIntervals.yearOverYear,
  ReportIntervals.customRange,
];

export const getRelativeDateFilter = (
  unitCount = 6,
  unit = RelativeDateFilterTimeUnit.Months,
  operator = RelativeDateOperators.InLast,
  target = {
    table: "Dates",
    hierarchy: "Dates Hierarchy",
    hierarchyLevel: "Date",
  }
): RelativeDateFilter => {
  const f = new RelativeDateFilter(target, operator, unitCount, unit, true);
  f.displaySettings = { isLockedInViewMode: true };
  return f;
};

export const getAdvancedDateFilter = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  target = {
    table: "Dates",
    hierarchy: "Dates Hierarchy",
    hierarchyLevel: "Date",
  }
): AdvancedFilter | undefined => {
  if (!startDate || !endDate) return undefined;
  const f = new AdvancedFilter(
    target,
    "And",
    {
      operator: "GreaterThanOrEqual",
      value: startDate,
    },
    { operator: "LessThanOrEqual", value: endDate }
  );
  f.displaySettings = { isLockedInViewMode: true };
  return f;
};

export const getIntervals = (
  sourceIntervals: string[],
  setDateFilterViews: (views: DateView[]) => void
) => {
  const now = DateTime.utc();

  const intervalMap: Record<ReportIntervals, { value: string; filter: IntervalType["filter"]; views?: DateView[] }> = {
    [ReportIntervals.lastMonth]: {
      value: "Last Month",
      filter: getAdvancedDateFilter(
        now.startOf("month").minus({ months: 1 }).toISO({ includeOffset: false }),
        now.startOf("month").minus({ days: 1 }).toISO({ includeOffset: false })
      ),
    },
    [ReportIntervals.lastThreeMonths]: {
      value: "Last 3 months",
      filter: getAdvancedDateFilter(
        now.minus({ months: 2 }).startOf("month").toISO({ includeOffset: false }),
        now.toISO()
      ),
    },
    [ReportIntervals.lastQuarter]: {
      value: "Last Quarter",
      filter: getAdvancedDateFilter(
        now.startOf("quarter").minus({ quarters: 1 }).toISO({ includeOffset: false }),
        now.endOf("quarter").minus({ quarters: 1 }).toISO()
      ),
    },
    [ReportIntervals.lastSixMonths]: {
      value: "Last 6 months",
      filter: getAdvancedDateFilter(
        now.minus({ months: 5 }).startOf("month").toISO({ includeOffset: false }),
        now.toISO()
      ),
    },
    [ReportIntervals.yearToDate]: {
      value: "Year to Date",
      filter: getRelativeDateFilter(1, RelativeDateFilterTimeUnit.Years, RelativeDateOperators.InThis),
    },
    [ReportIntervals.yearOverYear]: {
      value: "Year over Year",
      filter: getRelativeDateFilter(1, RelativeDateFilterTimeUnit.Years),
    },
    [ReportIntervals.customRange]: {
      value: "Custom Range",
      filter: undefined,
      views: ["year", "month"],
    },
    [ReportIntervals.customRangeWithDays]: {
      value: "Custom Range",
      filter: undefined,
      views: ["year", "month", "day"],
    },
  };

  return sourceIntervals
    .filter((key): key is ReportIntervals => key in intervalMap)
    .map((key) => {
      const { value, filter, views } = intervalMap[key];
      if (views) setDateFilterViews(views);
      return { value, filter };
    });
};
